// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * The gate. A row code is any non-blank string the installed content declares —
 * the platform validates that a code was *supplied*, never that it is one of a
 * list it holds.
 */
export function isToolkitRowCode(value) {
    return typeof value === 'string' && value.trim().length > 0;
}
/**
 * Brand a value already accepted by {@link isToolkitRowCode}, or read from a
 * document whose code was validated on the way in.
 *
 * Does not re-validate: a reader that brands a blank string has a defect at its
 * read, and hiding it behind a silent coercion here would move the defect to
 * where nobody is looking for it. Call {@link isToolkitRowCode} first.
 */
export function toolkitRowCode(value) {
    return value;
}
// ── Provenance ───────────────────────────────────────────────────
/**
 * Who or what produced a record here.
 *
 * `system_worker` and `migration` are separate values on purpose. A worker acted
 * on a live event; a migration classified something that already existed. An
 * auditor reading a trail is entitled to tell "the platform reacted to a fact"
 * from "the platform back-filled an interpretation", and AC10 forbids a
 * migration from ever presenting itself as the first.
 */
export const COMPLIANCE_ACTOR_KINDS = [
    'user',
    'service_account',
    'system_worker',
    'migration',
];
// ── 1. ComplianceTriggerEvent ────────────────────────────────────
/**
 * Why an event carries no `occurred_at`.
 *
 * Three values, because the remedies differ. `source_field_empty` means the
 * record has the field and nobody filled it — a person can fix that.
 * `source_field_absent` means the record does not carry the field at all, which
 * is a mapping defect in whoever emitted the event and no operator can fix it
 * from a screen. `source_record_unreadable` means the platform could not read
 * the record, so it makes **no statement** about the timestamp either way.
 *
 * Collapsing them yields "no date" in three situations with three different
 * owners, which is the empty state nobody can act on that
 * `CAPABILITY_ABSENCE_REASONS` exists to prevent.
 */
export const TRIGGER_TIMESTAMP_ABSENCE_REASONS = [
    'source_field_empty',
    'source_field_absent',
    'source_record_unreadable',
];
/**
 * How far the recorded instant can be relied on.
 *
 * Mirrors {@link RegulatoryClockProvisionalReason} at the *event* layer, so a
 * clock resolved from this event can be marked provisional for a reason that
 * traces to the fact rather than to the resolver's guess.
 */
export const TRIGGER_TIMESTAMP_CERTAINTY = [
    /** Recorded by the platform when the fact occurred. */
    'recorded',
    /** Entered afterwards by a person reconstructing when it happened. */
    'reconstructed',
    /** Recorded, and someone has since disputed it. */
    'disputed',
];
/**
 * The lifecycle of an outbox row.
 *
 * `pending_domain_commit` is the first state and it is the whole reason this
 * enumeration is not four values. Sprint 65.1.0's P0-D measured that
 * `withTransaction` is unavailable by deployment assumption
 * (`classification-service.ts`, `trust-service.ts`) and that the test double has
 * no rollback, so a test asserting atomicity would pass without proving it. The
 * shipped pattern is therefore **compare-and-set plus compensation**, and it
 * needs a state that means *"this event describes a domain mutation that has not
 * been confirmed committed"*:
 *
 * 1. write the event `pending_domain_commit`;
 * 2. perform the domain mutation;
 * 3. CAS the event to `pending` — only a committed mutation reaches this;
 * 4. if step 2 failed, compensate by CAS to `discarded` with a reason.
 *
 * A row left in `pending_domain_commit` past its window is a *known* unknown: a
 * sweeper re-reads the domain record and either promotes or discards it. That is
 * an honest recovery path. A three-state enumeration would have had to write the
 * event as `pending` before the mutation — creating obligations for mutations
 * that never happened — or after it, silently dropping every trigger whose
 * process died in between.
 */
export const TRIGGER_EVENT_STATUSES = [
    'pending_domain_commit',
    'pending',
    'leased',
    'consumed',
    'failed',
    'discarded',
];
export const TRIGGER_EVENT_DISCARD_REASONS = [
    'domain_mutation_not_committed',
    'no_binding_matched',
    'superseded_by_later_version',
    'binding_disabled',
    'pack_reference_mode',
];
/**
 * Why a binding's selector names no installed source — Sprint 65.2.0 (AC2).
 *
 * A selector that selects nothing is not one statement. It is two, with two
 * different owners, and the register has to be able to tell them apart:
 *
 * - `content_declares_no_selector` — the toolkit row's own declaration named no
 *   capability, framework, deadline event type or record. Nobody looked at the
 *   installed corpus, because the content said there was nothing to look for;
 * - `measured_no_statutory_period` — the declaration DID name a selector, the
 *   installer resolved it against this organisation's installed content through
 *   the same reader the obligation resolver uses, and **no reachable record
 *   states a period this platform can express**. The binding is cadence-only by
 *   measurement.
 *
 * The second is the value AC2 turns on. "These four rows are cadence-only" is a
 * claim about a corpus, and a claim about a corpus that is not written down
 * beside the record it produced is indistinguishable from an assumption. An
 * installer that simply authored an empty selector would produce exactly the
 * same binding for "we measured and found nothing" and for "we never looked".
 *
 * - `reference_mode_only` — Sprint 63.31.0 (#406). The declaration named a
 *   selector and the installer measured it, but every record it reaches belongs
 *   to an overlay installed FOR REFERENCE (owner ruling OD8 (a): such an overlay
 *   runs no register clocks). That is not "the content states no period": the
 *   content may state one, and the organisation chose not to operate it. Read
 *   through `declarationsForSelectorWithAbsence` and `notRunReasonOf`; a
 *   selector whose only declaring frameworks are retired or superseded keeps
 *   `measured_no_statutory_period` (decision E11).
 *
 * Non-null **exactly when** the selector selects nothing; null whenever it names
 * anything at all.
 */
export const BINDING_SELECTOR_ABSENCE_REASONS = [
    'content_declares_no_selector',
    'measured_no_statutory_period',
    'reference_mode_only',
];
/**
 * A content declaration that the instrument itself sets no clock — Sprint 65.11.0.
 *
 * `reason` is the half that changes behaviour and `citation` is the half that
 * makes it checkable. Both are required: the whole point is that these two
 * reasons are claims about a law, and a claim about a law with no provision
 * behind it is the assertion this programme has already shipped wrong.
 */
export const COMPLIANCE_STATUTORY_SILENCE_REASONS = [
    'instrument_states_no_period',
    /*
     * Sprint 65.18.0 — RESTORED, on the terms its removal recorded.
     *
     * The docblock on `OBLIGATION_STATUTORY_ABSENCE_REASONS` removed it
     * because "nothing can declare it" and named the row it would fit:
     * "the only row it would fit — IRP-01 — has no binding, because it has
     * no domain fact for one to watch. … It comes back with its first real
     * declaration, not before."
     *
     * 65.18.0 gives IRP-01 that fact and that binding, so the condition is
     * met. Re-verified against the SIGNED sources rather than the pack
     * (65.16.1's lesson): `diễn tập`, `dự phòng` and continuity phrasing
     * appear ZERO times in law-91-2025-text-vi.txt and decree-356-ocr-vi.txt.
     * The only adjacent text is a DPIA annex field assessing incident-response
     * capability, which is an assessment input rather than a duty to exercise.
     *
     * It is NOT interchangeable with the member above. "We owe this, on no
     * clock" and "this is not required of us" are different answers, and
     * collapsing them loses the difference an operator most needs.
     */
    'instrument_imposes_no_duty',
];
/**
 * How a cadence recurs. Calendar arithmetic only.
 *
 * There is deliberately no `day_type` here, which {@link RegulatoryClockPeriod}
 * does have. Working days are something an instrument states, and a cadence has
 * no instrument behind it to ask — offering the field would invite a cadence to
 * be authored as though it did.
 */
export const CADENCE_RECURRENCE_UNITS = ['day', 'week', 'month', 'quarter', 'year'];
/**
 * What a material change does to a running cadence.
 *
 * `no_effect` is a real, chosen answer and not the absence of a policy — a
 * binding that declares no material-change policy at all carries
 * `material_change_rule: null`, which is a different statement.
 */
export const MATERIAL_CHANGE_EFFECTS = [
    'reset_cadence',
    'raise_new_obligation',
    'no_effect',
];
/**
 * Whether a binding may create operational obligations at all.
 *
 * `reference` packs are installed for reading and comparison and must remain
 * operationally inert (parent contract, 65.1.0 delivery notes). A binding in
 * reference mode still resolves, still shows what it *would* do, and creates
 * nothing — which is why this is a mode on the binding rather than the absence
 * of a binding.
 */
export const BINDING_OPERATIONAL_MODES = ['operational', 'reference'];
/**
 * Why resolution produced no statutory clock.
 *
 * Every value is a different statement and none of them is a blank date, in the
 * `PUBLICATION_DERIVED_DATE_UNDATED_REASONS` tradition:
 *
 * - `selector_resolved_no_deadline_record` — the organisation's installed
 *   content declares no period for this duty. Nothing is wrong with the
 *   platform; the remedy is content;
 * - `deadline_record_states_no_period` — a record was found and it carries no
 *   figure. {@link RegulatoryClock.period} is null and no default is applied,
 *   ever (AC3);
 * - `trigger_timestamp_absent` — a period exists and there is no anchor to count
 *   from, because the event carries no `occurred_at`;
 * - `framework_not_in_force` — the declaring framework does not bind yet; the
 *   obligation says when it will;
 * - `framework_record_unreadable` — the framework record could not be read, so
 *   **no statement** is made about its timing. This must never fall through to
 *   an in-force reading, which is the whole content of `DeclarationTiming`;
 * - `cadence_only_obligation` — there is no statutory clock *by design*. The
 *   duty is an internal control. This is the value AC4's "a cadence-only
 *   obligation exposes no statutory due date" test asserts;
 * - `applicability_facets_matched_no_record` — the binding declared facets
 *   (Sprint 65.4.0 AC1) and this event's facts matched none of the records the
 *   selector reached. Distinct from `selector_resolved_no_deadline_record`,
 *   which says the content declares nothing at all: here the content declares
 *   periods and states that none of them applies to THIS event. Never widened
 *   back to all — widening is the defect the facets exist to remove.
 *
 * ## Sprint 65.11.0 — the two values that say the LAW was read
 *
 * Every reason above is a statement about the platform or the content. None of
 * them can say *the instrument itself was read and this is what it says*, and
 * the Phase 28 release gate scored that silence as a failed condition for
 * eighteen of twenty-three toolkit rows.
 *
 * It is not a failure. A law that sets no deadline is a determinate answer, and
 * the correct executable behaviour is that nothing is overdue **and the screen
 * says why, naming the provision it read**. These two carry that:
 *
 * - `instrument_states_no_period` — the instrument imposes the duty and states
 *   **no countable period** for it. Vietnam's corpus says this often: `định kỳ`
 *   — "periodically" — with no interval, which is a real duty on no clock;
 * ### The member that is NOT here, and why
 *
 * A second value — *the instrument imposes no such duty at all* — was drafted
 * alongside it, and it is a genuinely different answer: Sprint 65.9.0
 * established exactly that for incident-response exercises, where `diễn tập`
 * appears nowhere in either instrument. Collapsing the two loses the difference
 * between "we owe this, on no clock" and "this is not required of us".
 *
 * It was removed before merge because **nothing can declare it**. A silence is
 * declared on a binding, and the only row it would fit — IRP-01 — has no
 * binding, because it has no domain fact for one to watch. The
 * union-reachability census caught it as written by no route-reachable path,
 * and this repository's rule is settled: Sprint 65.4.0 deleted `acknowledged`
 * and `failed` from `ComplianceExternalAction` for the same reason, because the
 * census "permits no 'a later sprint will write it'".
 *
 * It comes back with its first real declaration, not before.
 *
 * Neither may be written from an absence of evidence. Both are declared by
 * installed content that cites the provision actually read, and the content
 * validator refuses a declaration with no citation — an unread instrument
 * remains `selector_resolved_no_deadline_record`, which says only that the
 * content is silent.
 */
export const OBLIGATION_STATUTORY_ABSENCE_REASONS = [
    'selector_resolved_no_deadline_record',
    'deadline_record_states_no_period',
    'trigger_timestamp_absent',
    'framework_not_in_force',
    'framework_record_unreadable',
    'cadence_only_obligation',
    'applicability_facets_matched_no_record',
    'instrument_states_no_period',
    // Sprint 65.18.0 — see the note on COMPLIANCE_STATUTORY_SILENCE_REASONS.
    // Declared by IRP-01's binding, which is the first that can.
    'instrument_imposes_no_duty',
];
/**
 * Where a cadence anchor is counted from.
 *
 * `last_completion` and `obligation_created` are different anchors and the
 * difference is visible: a control completed late either restarts from the
 * completion or stays on its original grid, and which one the organisation
 * chose is a policy decision that must be recorded rather than assumed.
 */
export const CADENCE_ANCHOR_KINDS = [
    'obligation_created',
    'last_completion',
    'linked_record_effective_date',
    'fixed_date',
];
/**
 * Why a cadence has no next occurrence.
 *
 * Deliberately a separate vocabulary from
 * {@link ObligationStatutoryAbsenceReason}. They are absences of different
 * things, and one enumeration serving both would be the first step toward the
 * two timings sharing a representation.
 */
export const CADENCE_ABSENCE_REASONS = [
    /** The anchor date is not available, so there is nothing to count from. */
    'anchor_unresolved',
    /** The organisation paused this cadence. A decision, not a gap. */
    'paused_by_organisation',
    /** The obligation is closed; the cadence stops rather than rolling forward. */
    'obligation_closed',
];
/** Who decided this cadence. */
export const CADENCE_SOURCES = [
    /** The organisation set it. */
    'organisation_policy',
    /** Taken from the binding's `cadence_rule` default. */
    'binding_default',
    /**
     * Installed content *recommends* it without making it binding.
     *
     * A recommendation is still not law. If installed content makes a period
     * binding, it is a {@link RegulatoryClock} resolved through the selector and
     * it does not appear here at all — that boundary is the parent contract's
     * "store as `recommended_control_cadence` unless an installed sector rule
     * makes it statutory", and this value is where the mistake would be made.
     */
    'pack_recommendation',
];
/**
 * How an obligation stands. Deliberately short, and deliberately without a
 * value meaning "done because someone said so".
 *
 * `closed` is reachable only with a {@link ComplianceExecutionProof} on the
 * record; `superseded` covers a later source version replacing this instance;
 * `blocked` and `awaiting_prerequisite` are separate because the first is a
 * decided fact about a failing gate and the second is a gate nobody has
 * evaluated yet — the distinction this whole file is built around.
 */
export const OBLIGATION_STATES = [
    'open',
    'awaiting_prerequisite',
    'blocked',
    'closed',
    'superseded',
    'not_applicable',
];
/**
 * The states in which a duty is still somebody's to do.
 *
 * fix-sprint-20260904-2120 F31. This set was spelled FOUR times across three
 * backend files — `obligation-service.ts`'s overdue filter,
 * `obligation-worker.ts`'s due scan, `coverage-service.ts`'s `live` sum, and
 * the same file's inverse exclusion list — so a seventh member of
 * `ObligationState` had to be remembered in four places, by four readers, with
 * nothing to say when one was missed.
 *
 * Derived from `OBLIGATION_STATES` by EXCLUSION rather than listed, so a state
 * added to the union is live until someone says otherwise: the safe default for
 * a register is that a new duty state is one a person still owes, not one that
 * silently disappears from every overdue count. The exhaustive
 * `TERMINAL_OBLIGATION_STATES` beside it is what makes that a decision rather
 * than an omission — adding a state to the union without classifying it here
 * makes it live, adding it there makes it finished, and `ObligationState`'s own
 * type checking forces the author to look.
 */
export const TERMINAL_OBLIGATION_STATES = ['closed', 'superseded', 'not_applicable'];
/** A duty still open, waiting on a prerequisite, or blocked. */
export const OBLIGATION_LIVE_STATES = OBLIGATION_STATES.filter((s) => !TERMINAL_OBLIGATION_STATES.includes(s));
/**
 * Why an obligation is blocked.
 *
 * `prerequisite_failed` and `prerequisite_unknown` are separate values for the
 * reason {@link CompliancePrerequisiteResult} exists: "we checked and it does
 * not hold" and "we could not check" have different owners and different
 * remedies, and merging them would let an unevaluated gate read as a decided
 * one — which fails open, in a module whose gates exist to fail closed.
 */
export const OBLIGATION_BLOCKED_REASONS = [
    'prerequisite_failed',
    'prerequisite_unknown',
    'source_record_unreachable',
    'binding_disabled',
    'pack_reference_mode',
];
/**
 * The DPO's relationship to one duty.
 *
 * `advises`, `monitors` and `challenges` are oversight; `executes` is the case
 * where the DPO has actually been assigned the work. They are separate values
 * because an aggregate view that showed them the same way would be the
 * responsibility drift the parent contract's risk table names. `none` is a
 * legitimate answer for a duty the DPO has no role in, and it is not the same as
 * `not_determined`, which says the binding has not been configured.
 */
export const DPO_OVERSIGHT_ROLES = [
    'advises',
    'monitors',
    'challenges',
    'executes',
    'none',
    'not_determined',
];
/**
 * How an obligation's link to its source was classified.
 *
 * Migration vocabulary, and it is AC10's whole content: an existing link is
 * classified, never repaired by invention. `provisional` means the relationship
 * is believed and the evidence for it is weaker than a live trigger;
 * `orphaned` means the source could not be found; `blocked` means it was found
 * and something prevents the obligation running. A migration that could write
 * `verified` over any of these would be synthesising the history the parent
 * contract's risk table forbids.
 */
export const OBLIGATION_LINEAGE_CLASSES = [
    'verified',
    'provisional',
    'orphaned',
    'blocked',
];
// ── 5. ComplianceExecutionProof ──────────────────────────────────
/**
 * How far a proof has been independently checked.
 *
 * Five values, and the two that look alike are the point. `not_verified` means
 * nothing has looked yet; `not_verifiable` means there is no independent check
 * for this kind of proof and there never will be. A reader who cannot tell them
 * apart cannot tell a backlog from a limitation.
 *
 * `operator_attested` exists because of the parent contract's provider boundary:
 * "A text field saying 'submitted,' 'published,' 'signed' or 'anonymized' is not
 * completion proof." Sometimes a named human's assertion is genuinely all there
 * is — an assisted-mode filing handed over a counter. That is recordable, and it
 * must be *labelled* as what it is rather than dressed as verification.
 */
export const PROOF_VERIFICATION_STATES = [
    'not_verified',
    'verified',
    'verification_failed',
    'operator_attested',
    'not_verifiable',
];
// ── 6. CompliancePrerequisiteResult ──────────────────────────────
/**
 * The three outcomes, and the reason there are three.
 *
 * `unknown` is not a shade of `fail` and it is emphatically not a shade of
 * `pass`. It says the platform could not determine the answer — the owning
 * module was unavailable, the record could not be read, or nothing has evaluated
 * it yet. A two-valued result forces that situation into one of the other two,
 * and whichever one it is forced into is wrong: as `pass` it opens a gate on no
 * evidence, as `fail` it blocks work for a reason nobody can remedy.
 *
 * Use {@link prerequisiteBlocks} rather than comparing against values by hand.
 */
export const PREREQUISITE_OUTCOMES = ['pass', 'fail', 'unknown'];
/**
 * Why an outcome is `unknown`. Required whenever it is.
 *
 * `not_yet_evaluated` is the honest first state of every prerequisite and must
 * never be confused with `evaluator_unavailable`, which is a fault someone has
 * to fix, or with `selector_resolved_nothing`, which is a content gap.
 */
export const PREREQUISITE_UNKNOWN_REASONS = [
    'not_yet_evaluated',
    'evaluator_unavailable',
    'source_record_unreadable',
    'selector_resolved_nothing',
];
/**
 * The fail-closed rule, in ONE place.
 *
 * A prerequisite blocks unless it explicitly passed. `unknown` blocks — the
 * whole reason `unknown` exists is that it is not a pass — and a caller writing
 * `outcome === 'fail'` by hand has written the fail-open version of this
 * function. The 64.35.0 lesson applies: a rule with three spellings has drifted
 * in two of them, and two of three agreeing is a coincidence.
 *
 * A non-blocking prerequisite never blocks whatever its outcome; that is
 * declared on the spec, and it is why the flag is copied onto the result.
 */
export function prerequisiteBlocks(result) {
    if (!result.blocking)
        return false;
    return result.outcome !== 'pass';
}
// ── 7. ComplianceExternalAction — Sprint 65.4.0 (AC3) ───────────
//
// ## What the foundation refused, and why this shape is SMALLER
//
// This contract was written in 65.1.0 and REMOVED again before it shipped.
// `scripts/union-reachability-census.ts` failed it: all eight members of its
// status union — prepared, dispatched, acknowledged, receipt_recorded,
// rejected, failed, reconciliation_required, abandoned — were "written by no
// route-reachable path". That was correct and not a false positive: the
// foundation mounted no external action on purpose, so nothing could produce
// any of those states.
//
// The census's permitted exemption reasons are system_raised, initial_value,
// migration_only, seed_only and sweep_only. There is deliberately no "a later
// sprint will write it", and inventing one would turn the register into the
// allowlist it exists to abolish. **A vocabulary nobody can produce is not a
// contract; it is a promise typed out early.**
//
// So the union below is shorter than the one that was removed, and that is the
// point: every member names a state a mounted route in this sprint writes.
// `acknowledged` is gone — it was a second name for a receipt whose
// classification already says so. Members are added when a writer lands, never
// in anticipation of one.
/**
 * How the submission left this product.
 *
 * **`assisted` never claims automation.** No code path in this codebase
 * contacts a government endpoint — measured across every outbound call in the
 * backend, where every such string is prose on a cover letter or a test
 * fixture. Assisted means the product prepared the artefact, recorded who is
 * authorised to send it, and recorded what came back; the operator transmitted
 * it. A submission with no recorded transmission evidence must never reach a
 * state that reads as filed.
 *
 * `provider` means a transport this product operates end to end and can produce
 * a machine receipt for. It exists because one such engine already ships.
 */
export const COMPLIANCE_EXTERNAL_ACTION_MODES = ['assisted', 'provider'];
/**
 * The states a mounted route writes. Nothing here is aspirational.
 *
 * - `prepared` — the artefact and the authorised sender are recorded and
 *   nothing has left. The create path's initial value;
 * - `dispatched` — transmission was asserted (assisted) or performed
 *   (provider). **Not** "filed": nobody outside has said anything yet;
 * - `receipt_recorded` — something came back and is classified. What it MEANS
 *   is the receipt's classification, not the status. An acknowledgement and a
 *   rejection are both receipts, and collapsing them into the status is exactly
 *   how `acknowledged` became a second name for one;
 * - `abandoned` — the operator stopped. Terminal, and honest about never having
 *   been filed.
 *
 * There is no `failed`. A provider transport failure leaves the action
 * `prepared` with the attempt recorded, because a failed dispatch did not
 * happen — a status asserting it did would be the false claim this mechanism
 * exists to prevent.
 */
export const COMPLIANCE_EXTERNAL_ACTION_STATUSES = [
    'prepared',
    'dispatched',
    'receipt_recorded',
    'abandoned',
];
/**
 * What came back, and what it means.
 *
 * Generalised from the shipped five-way authority-response classification
 * rather than invented beside it: that vocabulary is already in production and
 * already carries an append-only reclassification history, and a second
 * spelling would be free to drift from it.
 *
 * `no_response` is a recorded observation, not an absence. An operator stating
 * "nothing came back by the date we expected" is evidence, and it is what makes
 * a reconciliation actionable rather than merely overdue.
 */
export const COMPLIANCE_EXTERNAL_ACTION_CLASSIFICATIONS = [
    'acknowledgement',
    'acceptance',
    'rejection',
    'supplement_request',
    'no_response',
];
/**
 * Whether this product's record and the outside world are known to agree.
 *
 * `not_required` is the create path's initial value. `required` is written when
 * a dispatch has no receipt by the date the operator expected one — a state a
 * sweep raises, which is why it is a legitimate `sweep_only` member rather than
 * an aspiration. `resolved` is written when a receipt or an abandonment settles
 * it.
 */
export const COMPLIANCE_EXTERNAL_ACTION_RECONCILIATIONS = [
    'not_required',
    'required',
    'resolved',
];
// ── 8. Coverage — the computed 23-row readiness ──────────────────
//
// Declared HERE, not in `coverage-service.ts`, and the reason is this sprint's
// own scar tissue. `TaskSourceModule` shipped as a hand-copied SECOND spelling
// in `task-management/schemas.ts`: adding a member to the union type-checked
// everywhere while the API went on refusing it, silently. This contract was
// heading the same way — the service declared it and `api-client.ts` restated
// it, so the wire had two spellings free to drift with nothing to notice.
//
// One declaration, imported by both ends.
/**
 * How ready one toolkit row is, computed. Never stored, never editable.
 *
 * Ordered from "nothing has been asked" to "everything asked for is recorded".
 * The order is not a ranking of importance — it is the order a remedy becomes
 * available in, which is what a work programme is read for.
 */
export const COMPLIANCE_COVERAGE_READINESS = [
    /** The organisation's installed content declares no binding for this row. */
    'no_binding',
    /** Bindings exist and every one is reference-mode or disabled — inert by design. */
    'inert',
    /** A binding is live and no fact has yet started a duty under it. */
    'not_triggered',
    /** At least one duty is running and at least one is blocked. */
    'blocked',
    /** Duties are running and none is blocked; evidence is still outstanding. */
    'running',
    /** Every duty raised under this row is closed against a recorded proof. */
    'closed',
];
// ── Collections ──────────────────────────────────────────────────
export const COMPLIANCE_TRIGGER_EVENTS_COLLECTION = 'grc_compliance_trigger_events';
export const COMPLIANCE_TRIGGER_BINDINGS_COLLECTION = 'grc_compliance_trigger_bindings';
export const COMPLIANCE_OBLIGATIONS_COLLECTION = 'grc_compliance_obligations';
export const COMPLIANCE_EXECUTION_PROOFS_COLLECTION = 'grc_compliance_execution_proofs';
/** The installed toolkit row register — Sprint 65.2.0, AC1. */
export const COMPLIANCE_TOOLKIT_ROWS_COLLECTION = 'grc_compliance_toolkit_rows';
/** External submissions and what came back — Sprint 65.4.0, AC3. */
export const COMPLIANCE_EXTERNAL_ACTIONS_COLLECTION = 'grc_compliance_external_actions';
/**
 * Sprint 63.31.0 (#413, owner ruling Q1 (a), second sub-ruling; decision E3) —
 * the duties a fact WOULD have raised, held while every source the binding
 * reaches is not yet in force. One row per (binding, trigger event), unique.
 */
export const COMPLIANCE_PARKED_OBLIGATIONS_COLLECTION = 'grc_compliance_parked_obligations';
// ── Errors ───────────────────────────────────────────────────────
/**
 * Every error this module can produce, one code per distinct condition.
 *
 * The rule and its reason come from `CLASSIFICATION_ERROR_CODES`: a code that
 * means five things is a distinction no consumer can make, and a code raised at
 * two HTTP statuses leaves a caller unable to tell "your request was wrong" from
 * "this tenant's installed content is wrong" — the first is fixed by changing a
 * request and the second cannot be retried at all.
 */
export const COMPLIANCE_OPERATIONS_ERROR_CODES = {
    /** No such obligation in this organisation — a `404`, including cross-org. */
    OBLIGATION_NOT_FOUND: 'compliance_obligation_not_found',
    BINDING_NOT_FOUND: 'compliance_binding_not_found',
    TRIGGER_EVENT_NOT_FOUND: 'compliance_trigger_event_not_found',
    PROOF_NOT_FOUND: 'compliance_execution_proof_not_found',
    /**
     * The binding's selector names nothing any installed framework declares — a
     * `409`, meaning *install or repair content*. Never a bad request.
     */
    SELECTOR_RESOLVES_NOTHING: 'compliance_selector_resolves_nothing',
    /** A binding whose selector sets no capability, code, event type or ids. */
    SELECTOR_EMPTY: 'compliance_selector_empty',
    /**
     * A caller tried to close an obligation without a
     * {@link ComplianceExecutionProof}. A `400`: the domain service owns
     * completion, and this module has no way to assert one.
     */
    COMPLETION_REQUIRES_PROOF: 'compliance_completion_requires_proof',
    /** A blocking prerequisite did not pass. See {@link prerequisiteBlocks}. */
    PREREQUISITE_BLOCKED: 'compliance_prerequisite_blocked',
    /**
     * A binding in `reference` mode was asked to create an operational
     * obligation. Reference content is inert by contract, so this is refused
     * rather than downgraded.
     */
    BINDING_REFERENCE_MODE: 'compliance_binding_reference_mode',
    /** No such external action in this organisation — a `404`, including cross-org. */
    EXTERNAL_ACTION_NOT_FOUND: 'compliance_external_action_not_found',
    /**
     * The same `idempotency_key` was submitted again with a DIFFERENT body — a
     * `409`. Sprint 65.4.0 (AC3).
     *
     * Distinct from a replay: an identical resubmission returns the existing
     * action, because that is the caller retrying a timeout and the whole point
     * of the key. A key reused for different content is a caller bug, and
     * answering it with the first action would silently discard the second
     * submission.
     */
    EXTERNAL_ACTION_IDEMPOTENCY_CONFLICT: 'compliance_external_action_idempotency_conflict',
    /**
     * A retry was asked for an action the authority ACCEPTED — a `409`.
     *
     * Re-filing something already accepted is not a retry; it is a second filing,
     * and the two mean different things to a regulator. The remedy is a new
     * submission stating why, not a retry link.
     */
    EXTERNAL_ACTION_RETRY_OF_SUCCESS: 'compliance_external_action_retry_of_success',
    /**
     * A caller tried to move an action to a state that reads as filed without
     * recorded transmission evidence — a `400`. Sprint 65.4.0 (AC3).
     *
     * This is the guard behind "assisted mode never claims automation": nothing
     * in this product contacts an authority, so *something an operator recorded*
     * is the only thing that can make a submission real.
     */
    EXTERNAL_ACTION_TRANSMISSION_UNVERIFIED: 'compliance_external_action_transmission_unverified',
    /**
     * The act asked for is not one this action's current status permits — a
     * `409`. Sprint fix-20260905-2135.
     *
     * `dispatch`, `recordReceipt` and `abandon` used to share one guard that
     * refused nothing but `abandoned`, so a dispatch could be replayed against an
     * action that already carried the authority's receipt: `dispatched_at` was
     * overwritten with a *later* operator-stated instant and `reconciliation`
     * reset to `required`, while the receipt stayed on the document. The record
     * then claimed a submission left the organisation after the receipt for it
     * was logged. `dispatched_at` is what an operator points at to say when
     * something was filed, so a transition that can rewrite it is a transition
     * that can falsify a statutory-filing record.
     *
     * The refusal names the current status, because "which state is it in" is the
     * only thing the caller needs in order to act.
     */
    EXTERNAL_ACTION_TRANSITION_REFUSED: 'compliance_external_action_transition_refused',
    /**
     * The four-field identity already exists. Not an error the caller should
     * retry differently — it is AC5 working, and the existing obligation is
     * returned rather than a second one created.
     */
    OBLIGATION_ALREADY_EXISTS: 'compliance_obligation_already_exists',
    /**
     * An external action was submitted with an `idempotency_key` already used in
     * this organisation for a different request.
     */
    IDEMPOTENCY_KEY_CONFLICT: 'compliance_idempotency_key_conflict',
    /** An assisted external action with no named human sender. */
    ASSISTED_ACTION_REQUIRES_SENDER: 'compliance_assisted_action_requires_sender',
    /** A `toolkit_row_code` that is blank. See {@link isToolkitRowCode}. */
    INVALID_TOOLKIT_ROW_CODE: 'compliance_invalid_toolkit_row_code',
    // ── Sprint 65.2.0 ────────────────────────────────────────────────
    /**
     * The organisation has installed no content declaring a toolkit row, so
     * there is nothing to install bindings from.
     *
     * A `409`, in the `SELECTOR_RESOLVES_NOTHING` tradition: the request was
     * well formed and the remedy is *install content*, not *change the request*.
     * Emphatically **not** a `404` and not an empty success — an installer that
     * answered "installed 0 bindings, all good" for a tenant with no toolkit is
     * the empty state nobody can act on.
     */
    TOOLKIT_NOT_INSTALLED: 'compliance_toolkit_not_installed',
    /**
     * The installed toolkit content declares rows this platform cannot install —
     * a blank row code, two records claiming one row, a target module the
     * platform does not have, a cadence with no label.
     *
     * A `409`, and the whole install is refused rather than the parseable rows
     * being taken: a half-installed register is a state nobody can reason about,
     * and "install what parses" makes a content defect look like a row the
     * toolkit does not have. The per-row diagnosis is in `details.defects[].code`
     * — that is the vocabulary a consumer branches on, never this one code.
     */
    TOOLKIT_DECLARATION_DEFECTIVE: 'compliance_toolkit_declaration_defective',
    /**
     * A proof was offered for a domain module that has registered no
     * {@link ComplianceDomainResult} source, so nothing can vouch that the domain
     * record says what the proof claims it says.
     *
     * A `409`, and it fails closed on purpose: the coordinator persists and links
     * proofs, it does not author them. A coordinator that accepted an unvouched
     * domain result would be asserting a completion it has no way to check —
     * which is the whole failure mode `ComplianceExecutionProof` exists to
     * prevent.
     */
    PROOF_DOMAIN_UNVERIFIED: 'compliance_proof_domain_unverified',
    /**
     * A proof cited no artefact, file or export. A `400`: the parent contract
     * says a text field saying "submitted" is not completion proof, and a proof
     * that points at nothing re-readable is that text field with more fields
     * around it.
     */
    PROOF_ARTEFACT_REQUIRED: 'compliance_proof_artefact_required',
    /**
     * A proof cited a file or artefact reference that does not resolve to a
     * record in the caller's organisation — fix-sprint-20260905-2135-followup-2
     * F6.
     *
     * `PROOF_ARTEFACT_REQUIRED` above counted the references and stopped there,
     * so `{"file_refs": ["x"]}` satisfied it and, with `close: true`, discharged
     * the obligation against a string. Five routes forward these arrays straight
     * off the request body. Counting a reference is not reading one.
     *
     * A `409`, and `details.unresolved` names every reference that failed rather
     * than the first, so an operator correcting a bundle resubmits once.
     *
     * ## What this does and does not cover
     *
     * `file_refs` resolve against the platform attachment store and
     * `artifact_refs` against {@link REGULATORY_ARTIFACT_COLLECTION} — both are
     * platform infrastructure with one canonical home, so resolving them requires
     * no guess about where a domain module keeps its records (the constraint
     * {@link DomainProofSource} exists to respect).
     *
     * `export_refs` have no such canonical store on this tree, so they are **not**
     * resolved. A proof must therefore cite at least one reference of a
     * resolvable kind — an `export_refs`-only bundle is refused with this code
     * rather than admitted unchecked, because admitting it would leave the same
     * hole one field over. Giving exports a canonical store, and then resolving
     * them, is the follow-up this deliberately does not pretend to have done.
     */
    PROOF_REF_UNRESOLVABLE: 'compliance_proof_ref_unresolvable',
    /**
     * The durable audit write for a proof did not land, so the proof would have
     * carried an empty `audit_event_ids`. A `503`: nothing is wrong with the
     * request and retrying it later is the remedy. The proof is **not** written —
     * a completion an inspection cannot see is not a completion.
     */
    PROOF_AUDIT_UNWRITTEN: 'compliance_proof_audit_unwritten',
    /**
     * A close was attempted against a proof that exists but cites a different
     * obligation. A `400`, and it is not pedantry: without it, one recorded proof
     * could close every obligation in the tenant.
     */
    PROOF_OBLIGATION_MISMATCH: 'compliance_proof_obligation_mismatch',
    // ── Sprint 65.3.0 ────────────────────────────────────────────────
    /**
     * A proof was offered whose {@link ComplianceSourceRef} names a record other
     * than the one the obligation was raised from. A `409`.
     *
     * Distinct from {@link COMPLIANCE_OPERATIONS_ERROR_CODES.PROOF_OBLIGATION_MISMATCH},
     * which is about a proof citing a different **obligation**. This one is about
     * a proof citing a different **record**, and until 65.3.0 nothing checked it:
     * `record()` read the obligation, asked the owning module to read back
     * whatever record the CALLER named, and never compared the two. Any proof for
     * a registered module could close any obligation in the tenant that cited that
     * module, provided the record existed somewhere in it. The evidence would have
     * been genuine and about something else entirely — the failure mode that is
     * undetectable after the fact, because both halves are real.
     */
    PROOF_RECORD_MISMATCH: 'compliance_proof_record_mismatch',
};
const _cadenceIsNotAClock = true;
void _cadenceIsNotAClock;
const _clockIsNotACadence = true;
void _clockIsNotACadence;
const _obligationHasNoDueDate = true;
void _obligationHasNoDueDate;
const _cadenceHasNoDueDate = true;
void _cadenceHasNoDueDate;
//# sourceMappingURL=compliance-operations.js.map